import { useState, useCallback, useMemo, useEffect } from 'react'

import {
  ApplePayId,
  GooglePayId,
} from '@components/payment/PaymentButtons/constants'
import { CloseReasons } from '@components/shared/types'
import { useAuthContext } from '@contexts/AuthenticationContext'
import { useMultiBookingContext } from '@contexts/MultiBookingDataContext'
import { usePaymentMethodSelectionContext } from '@contexts/payment-method-selection-context'
import { useUserContext } from '@contexts/UserContext'
import { PaymentMethod } from '@graphql/queries/paymentMethods'
import { useAvailablePayment } from '@hooks/payment'
import Analytics from '@services/Analytics'
import { CheckoutTypeEnum } from '@services/Analytics/tracking-plan/segment'
import { useStripeContext } from '@services/stripe'
import { useAnalyticsStore } from '@state/use-analytics-store'
import {
  PaymentMethodKey,
  usePaymentMethodsStore,
} from '@state/use-payment-methods-store'

const ThirdPartyPayments: Record<string, PaymentMethod> = {
  google: {
    __typename: 'PaymentSource',
    id: GooglePayId,
    isDefault: true,
  },
  apple: {
    __typename: 'PaymentSource',
    id: ApplePayId,
    isDefault: true,
  },
}

export type UsePaymentMethodProps = {
  hideThirdPartyMethods?: boolean
  onlySelectedMethod?: boolean
  isPaymentMethodSelection?: boolean
  key: PaymentMethodKey
}
const usePaymentMethods = (
  {
    hideThirdPartyMethods = false,
    onlySelectedMethod = false,
    isPaymentMethodSelection = false,
    key,
  }: UsePaymentMethodProps = {
    key: 'default',
  }
) => {
  const { requestAuthentication } = useAuthContext()
  const { user } = useUserContext()
  const availablePayments = useAvailablePayment()

  const { paymentMethods = [], paymentMethodsLoading } = useStripeContext()
  const {
    isPaymentOpen,
    isAddPaymentOpen,
    setIsPaymentOpen,
    setIsAddPaymentOpen,
  } = usePaymentMethodsStore()
  const {
    selectedPaymentMethod: multiSelectedPayment,
    setSelectedPaymentMethod: multiSetSelectedPayment,
    poolId: multiListingId,
  } = useMultiBookingContext()

  const {
    selectedPaymentMethod: paymentMethodContextSelectedPayment,
    setSelectedPaymentMethod: paymentMethodContextSetSelectedPayment,
  } = usePaymentMethodSelectionContext()

  const { selectedPaymentMethod, setSelectedPaymentMethod, listingId } =
    useMemo(() => {
      if (isPaymentMethodSelection) {
        return {
          selectedPaymentMethod: paymentMethodContextSelectedPayment,
          setSelectedPaymentMethod: paymentMethodContextSetSelectedPayment,
          listingId: '',
        }
      }

      return {
        selectedPaymentMethod: multiSelectedPayment,
        setSelectedPaymentMethod: multiSetSelectedPayment,
        listingId: multiListingId,
      }
    }, [
      isPaymentMethodSelection,
      multiListingId,
      multiSelectedPayment,
      multiSetSelectedPayment,
      paymentMethodContextSelectedPayment,
      paymentMethodContextSetSelectedPayment,
    ])

  const defaultPaymentMethod = useMemo(() => {
    if (selectedPaymentMethod) return selectedPaymentMethod
    if (onlySelectedMethod) return
    let defaultMethod
    if (
      !paymentMethods.length &&
      availablePayments &&
      !hideThirdPartyMethods &&
      !!user
    ) {
      defaultMethod = ThirdPartyPayments[availablePayments]
    } else {
      defaultMethod = paymentMethods.find((x) => x.isDefault)
      if (!defaultMethod && paymentMethods.length > 0) {
        defaultMethod = paymentMethods[0]
      }
    }
    return defaultMethod
  }, [
    selectedPaymentMethod,
    onlySelectedMethod,
    paymentMethods,
    availablePayments,
    hideThirdPartyMethods,
    user,
  ])
  const [tempSelectedMethod, setTempSelectedMethod] = useState(
    selectedPaymentMethod
  )

  const { checkoutId, searchId } = useAnalyticsStore()

  useEffect(() => {
    if (defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod)
      setTempSelectedMethod(defaultPaymentMethod)
    }
  }, [defaultPaymentMethod, setSelectedPaymentMethod, setTempSelectedMethod])

  const openPaymentMethods = useCallback(() => {
    requestAuthentication({
      purpose: 'paymentMethods',
      onResolve: () => {
        if (!isPaymentOpen[key]) {
          setIsPaymentOpen(key, true)
          Analytics.trackEvent('UserOpenedPaymentMethodChangeFromCheckout', {
            checkoutId: checkoutId ?? '',
            existingPaymentId: String(selectedPaymentMethod?.id),
            existingPaymentIsApplePay: selectedPaymentMethod?.id === ApplePayId,
            existingPaymentIsGooglePay:
              selectedPaymentMethod?.id === GooglePayId,
            paymentIdsAvailable: paymentMethods.map(({ id }) => id),
            listingId: listingId ?? '',
            searchId: searchId ?? '',

            isApplePay: false,
            isGooglePay: false,
            isMultiDip: false,
            newPaymentId: '',
          })
        }
      },
    })
  }, [
    requestAuthentication,
    isPaymentOpen,
    setIsPaymentOpen,
    key,
    checkoutId,
    selectedPaymentMethod?.id,
    paymentMethods,
    listingId,
    searchId,
  ])

  const closeIsPaymentOpen = useCallback(() => {
    setIsPaymentOpen(key, false)
    setTempSelectedMethod(selectedPaymentMethod)
  }, [setIsPaymentOpen, selectedPaymentMethod, key])

  const openAddPaymentMethod = useCallback(() => {
    if (!isAddPaymentOpen[key]) {
      setIsAddPaymentOpen(key, true)
      setIsPaymentOpen(key, false)
      Analytics.trackEvent('UserPressedAddPaymentMethod', {
        checkoutId: checkoutId ?? '',
        listingId: listingId ?? '',
        searchId: searchId ?? '',

        isApplePay: false,
        isGooglePay: false,
        isMultiDip: false,

        // TODO: matched schema
        newPaymentId: '',
        existingPaymentId: '',
        enteredFrom: '',
      })
    }
  }, [
    isAddPaymentOpen,
    setIsAddPaymentOpen,
    setIsPaymentOpen,
    key,
    checkoutId,
    listingId,
    searchId,
  ])

  const closeIsAddPaymentOpen = useCallback(
    (reason: CloseReasons) => {
      if (reason === CloseReasons.BUTTON) {
        setIsPaymentOpen(key, true)
      }
      setIsAddPaymentOpen(key, false)
    },
    [setIsAddPaymentOpen, setIsPaymentOpen, key]
  )

  const onCardUpdate = useCallback(() => {
    if (tempSelectedMethod) {
      setIsPaymentOpen(key, false)
    }

    setSelectedPaymentMethod(tempSelectedMethod)
    Analytics.trackEvent('UserSetPaymentMethodInWallet', {
      checkoutId: checkoutId ?? '',
      checkoutType: CheckoutTypeEnum.StandardBooking,
      existingPaymentId: String(selectedPaymentMethod?.id),
      newPaymentId: String(tempSelectedMethod?.id),
      isApplePay: tempSelectedMethod?.id === ApplePayId,
      isGooglePay: tempSelectedMethod?.id === GooglePayId,
      listingId: listingId ?? '',
      searchId: searchId ?? '',
      isMultiDip: false,
    })
  }, [
    tempSelectedMethod,
    setSelectedPaymentMethod,
    checkoutId,
    selectedPaymentMethod?.id,
    listingId,
    searchId,
    setIsPaymentOpen,
    key,
  ])

  const onTempMethodUpdate = useCallback(
    (newCard: PaymentMethod) => {
      setTempSelectedMethod(newCard)
      Analytics.trackEvent('UserChosePaymentMethodInWallet', {
        searchId: searchId ?? '',
        checkoutId: checkoutId ?? '',
        existingPaymentId: String(selectedPaymentMethod?.id),
        newPaymentId: String(newCard.id),
        isApplePay: newCard.id === ApplePayId,
        isGooglePay: newCard.id === GooglePayId,
        listingId: listingId ?? '',
        isMultiDip: false,
      })
    },
    [searchId, checkoutId, selectedPaymentMethod?.id, listingId]
  )

  const onAddPaymentMethod = useCallback(
    (newPaymentMethod: PaymentMethod) => {
      setIsPaymentOpen(key, false)
      setIsAddPaymentOpen(key, false)
      Analytics.trackEvent('UserSetPaymentMethodInWallet', {
        checkoutId: checkoutId ?? '',
        checkoutType: CheckoutTypeEnum.StandardBooking,
        existingPaymentId: String(selectedPaymentMethod?.id),
        newPaymentId: String(tempSelectedMethod?.id),
        isApplePay: tempSelectedMethod?.id === ApplePayId,
        isGooglePay: tempSelectedMethod?.id === GooglePayId,
        listingId: listingId ?? '',
        searchId: searchId ?? '',
        isMultiDip: false,
      })
      setTempSelectedMethod({
        ...newPaymentMethod,
        isNewPaymentId: true,
      })
      setSelectedPaymentMethod({
        ...newPaymentMethod,
        isNewPaymentId: true,
      })
    },
    [
      setIsPaymentOpen,
      setIsAddPaymentOpen,
      checkoutId,
      selectedPaymentMethod?.id,
      tempSelectedMethod?.id,
      listingId,
      searchId,
      setSelectedPaymentMethod,
      key,
    ]
  )

  return {
    selectedCard: selectedPaymentMethod,
    onCardUpdate,
    isPaymentOpen: isPaymentOpen[key],
    openPaymentMethods,
    closeIsPaymentOpen,

    isAddPaymentOpen: isAddPaymentOpen[key],
    tempSelectedMethod,
    openAddPaymentMethod,
    closeIsAddPaymentOpen,
    onTempMethodUpdate,
    onAddPaymentMethod,
    paymentMethodsLoading,
  }
}

export default usePaymentMethods
